import { getDeliveryByEventName, getProductImageURL } from '@noths/polaris-client-utils';
import type {
  ProductCardBadge,
  ProductCardProps,
} from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode, Price } from '@noths/polaris-dev-ts-types';
import { addDays } from 'date-fns';

import type { Product } from 'src/redux/products/types/Product';

/**
 * The card props that come from the product itself. The handlers, the favourite
 * state and `priority` are supplied by the call site, because those are about the
 * card's place on the page rather than about the product.
 */
export type ProductCardDataProps = Pick<
  ProductCardProps,
  'badge' | 'delivery' | 'isNew' | 'product' | 'rating'
>;

interface AdaptProductToProductCardPropsOptions {
  currencyCode: CurrencyCode;
  /**
   * Passed in so that a list of products dates every delivery from the same instant,
   * and so that the date is deterministic in tests. Defaults for a single call.
   */
  now?: Date;
}

/**
 * "Most Loved" is the customer-facing name for the `highly_rated` attribute,
 * matching the PDP's own badge rules and this app's PLP preview.
 */
const MOST_LOVED_BADGE: ProductCardBadge = { icon: 'mostLoved', label: 'Most Loved' };

const priceInCurrency = (prices: Price[] | null, currencyCode: CurrencyCode) =>
  prices?.find(({ currency }) => currency === currencyCode)?.amount;

/**
 * The card takes a structured date and cost and words the delivery line itself,
 * so every surface reads identically.
 *
 * Both parts are required, so a product missing either gets no delivery line.
 * Nothing is lost by that: the card drops a line it cannot date anyway. An
 * unpurchasable product is skipped too, since the card shows "Out of stock" in
 * that slot and ignores `delivery` entirely.
 */
const adaptDelivery = (
  { deliveryByEvent, deliveryCost, deliveryDays, purchasable }: Product,
  now: Date,
): ProductCardProps['delivery'] => {
  if (!purchasable || deliveryCost === null || deliveryDays === null) {
    return undefined;
  }

  /* The store holds the raw event key (`christmas`) and the card renders it as
     "Est to arrive by {eventName}", so it needs the display name. Not
     `getDeliveryByEventLabel`, which prefixes it ("Arrives by Christmas") and
     would read as "Est to arrive by Arrives by Christmas". The lookup is typed as
     always returning a value but is a plain object index, so an unrecognised key
     falls through to no event rather than to `undefined.name`. */
  const eventName = deliveryByEvent?.arriveByEvent
    ? getDeliveryByEventName(deliveryByEvent.eventName)?.name
    : undefined;

  return {
    cost: deliveryCost,
    date: addDays(now, deliveryDays),
    ...(eventName ? { eventName } : {}),
  };
};

/**
 * Maps a store product onto the design system `ProductCard`.
 *
 * Deliberately thin: sale state, the discount percentage and the out-of-stock
 * treatment are all derived by the card from `price`, `originalPrice` and
 * `availability`, so `onSale` and `salePercentage` are not passed through.
 */
export const adaptProductToProductCardProps = (
  product: Product,
  { currencyCode, now = new Date() }: AdaptProductToProductCardPropsOptions,
): ProductCardDataProps => {
  const {
    averageRating,
    code,
    detailPageHref,
    highlyRated,
    imageSrc,
    isNew,
    partner,
    preSalePrices,
    price,
    purchasable,
    title,
  } = product;

  const delivery = adaptDelivery(product, now);
  const originalPrice = priceInCurrency(preSalePrices, currencyCode);

  return {
    ...(highlyRated ? { badge: MOST_LOVED_BADGE } : {}),
    ...(delivery ? { delivery } : {}),
    isNew,
    product: {
      availability: purchasable ? 'in_stock' : 'out_of_stock',
      // The store keeps the numeric product code; the OOUX interface types it as a
      // string, since the basket API treats it as one.
      code: String(code),
      currencyCode,
      media: [
        {
          alt: title,
          type: 'image',
          /* `ProductMedia.url` must be fully resolved. The store holds the URL with
             its `{size}` token still in place, and the card does not resolve it. */
          url: getProductImageURL(imageSrc),
        },
      ],
      ...(originalPrice !== undefined ? { originalPrice } : {}),
      partner: { name: partner.name, shortcode: partner.shortcode },
      price: price.amount,
      title,
      url: detailPageHref,
    },
    ...(averageRating !== undefined ? { rating: averageRating } : {}),
  };
};
